/**
 * CounterSettings.tsx
 * Counterカードの設定ダイアログの中身（初期値・増分・最終値）
 */
import { useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';
import type { NodeSettingsProps } from '../../types';
import { useFlowDisplayLanguage } from '../../displayLanguage';
import { useNodeFieldState } from '../../hooks';
import { normalizeNumericInput } from '../../normalizeNumericInput';

const DEFAULT_INITIAL_VALUE = 0;
const DEFAULT_STEP_VALUE = 1;
const DEFAULT_FINAL_VALUE = 5;

const parseOrFallback = (raw: string, fallback: number): number => {
  const parsed = Number(normalizeNumericInput(raw));
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function CounterSettings({ node }: NodeSettingsProps<'counter'>) {
  const { updateNodeData } = useReactFlow();
  const displayLanguage = useFlowDisplayLanguage();
  const commitInitialValue = useCallback((raw: string) => {
    const next = parseOrFallback(raw, DEFAULT_INITIAL_VALUE);
    updateNodeData(node.id, { initialValue: next });
    return String(next);
  }, [node.id, updateNodeData]);

  const commitStepValue = useCallback((raw: string) => {
    const next = parseOrFallback(raw, DEFAULT_STEP_VALUE);
    updateNodeData(node.id, { stepValue: next });
    return String(next);
  }, [node.id, updateNodeData]);

  const commitFinalValue = useCallback((raw: string) => {
    const next = parseOrFallback(raw, DEFAULT_FINAL_VALUE);
    updateNodeData(node.id, { finalValue: next });
    return String(next);
  }, [node.id, updateNodeData]);
  const initialField = useNodeFieldState(
    String(node.data.initialValue ?? DEFAULT_INITIAL_VALUE),
    commitInitialValue,
  );
  const stepField = useNodeFieldState(
    String(node.data.stepValue ?? DEFAULT_STEP_VALUE),
    commitStepValue,
  );
  const finalField = useNodeFieldState(
    String(node.data.finalValue ?? DEFAULT_FINAL_VALUE),
    commitFinalValue,
  );

  return (
    <div className="space-y-4">
      <label className="block" data-product-tour="card-input-1">
        <span className="mb-1 block text-sm font-medium text-slate-700">
          {displayLanguage === 'en' ? 'start number' : 'はじめの数'}
        </span>
        <input
          type="text"
          inputMode="decimal"
          value={initialField.value}
          onChange={(event) => initialField.setValue(event.target.value)}
          onBlur={initialField.commit}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
      </label>
      <label className="block" data-product-tour="card-input-2">
        <span className="mb-1 block text-sm font-medium text-slate-700">
          {displayLanguage === 'en' ? 'count by' : 'ふえる数'}
        </span>
        <input
          type="text"
          inputMode="decimal"
          value={stepField.value}
          onChange={(event) => stepField.setValue(event.target.value)}
          onBlur={stepField.commit}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
      </label>
      <label className="block" data-product-tour="card-input-3">
        <span className="mb-1 block text-sm font-medium text-slate-700">
          {displayLanguage === 'en' ? 'end number' : 'おわりの数'}
        </span>
        <input
          type="text"
          inputMode="decimal"
          value={finalField.value}
          onChange={(event) => finalField.setValue(event.target.value)}
          onBlur={finalField.commit}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
      </label>
    </div>
  );
}
